/**
 * Specialized Error Boundary for Session Pages
 * Provides session-specific error recovery options
 */

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import ErrorFallback, { ErrorFallbackProps } from "./ErrorFallback";
import {
  getUserFriendlyErrorMessage,
  storeErrorForRecovery,
} from "../../utils/errorRecovery";

interface SessionErrorFallbackProps extends ErrorFallbackProps {
  sessionId?: string;
  onReturnHome?: () => void;
  onRetryConnection?: () => void;
}

const SessionErrorFallback: React.FC<SessionErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  sessionId,
  onReturnHome,
  onRetryConnection,
  ...props
}) => {
  const navigate = useNavigate();
  const friendlyMessage = getUserFriendlyErrorMessage(error);

  // Store error for recovery
  React.useEffect(() => {
    storeErrorForRecovery(error, { sessionId, timestamp: Date.now() });
  }, [error, sessionId]);

  const handleReturnHome = () => {
    if (onReturnHome) {
      onReturnHome();
    } else {
      navigate("/dashboard");
    }
  };

  const actions = (
    <div className="flex flex-col gap-2 mt-2">
      {onRetryConnection && (
        <button
          onClick={() => {
            onRetryConnection();
            resetErrorBoundary();
          }}
          className="bg-secondary text-surface py-2 px-4 rounded font-pixel shadow-pixel-shadow hover:bg-secondary-hover active:scale-95 transition-transform text-sm"
        >
          Retry Connection
        </button>
      )}
      <button
        onClick={handleReturnHome}
        className="bg-surface text-text-primary border-2 border-border py-2 px-4 rounded font-pixel shadow-pixel-shadow hover:bg-surface-hover active:scale-95 transition-transform text-sm"
      >
        Return to Dashboard
      </button>
    </div>
  );

  return (
    <ErrorFallback
      error={error}
      resetErrorBoundary={resetErrorBoundary}
      message={friendlyMessage}
      title="Session Error"
      actions={actions}
      {...props}
    />
  );
};

interface SessionErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onRetryConnection?: () => void;
  resetKeys?: Array<string | number>;
}

/**
 * Error boundary specifically for session pages
 * Provides session-specific recovery options
 */
const SessionErrorBoundary: React.FC<SessionErrorBoundaryProps> = ({
  children,
  onError,
  onRetryConnection,
  resetKeys,
}) => {
  const { sessionId } = useParams<{ sessionId: string }>();

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Store error for recovery
    storeErrorForRecovery(error, {
      sessionId,
      componentStack: errorInfo.componentStack,
      timestamp: Date.now(),
    });

    // Call optional error callback
    if (onError) {
      onError(error, errorInfo);
    }
  };

  return (
    <ErrorBoundary
      fallback={(props) => (
        <SessionErrorFallback
          {...props}
          sessionId={sessionId}
          onRetryConnection={onRetryConnection}
        />
      )}
      onError={handleError}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
};

export default SessionErrorBoundary;
